import * as React from 'react'
import { isDeepEqual } from '@applift/factor'
import { GridPrivateApiCommunity } from '../../../models/api/gridApiCommunity'
import { useGridApiMethod } from '../../utils/useGridApiMethod'
import { DataGridProcessedProps } from '../../../models/props/DataGridProps'
import { GridStateInitializer } from '../../utils/useGridInitializeState'
import { GridColumnWidthOffsetApi } from '../../../models/api/gridColumnWidthOffsetApi'
import { gridColumnOffsetSelector } from './columnWidthOffsetSelector'
import { ReactTableColumn } from '../../../models/gridReactTable'

const computeWidthOffset = (columns: ReactTableColumn[]) => {
  const colsWidthOffset: number[] = []
  columns?.reduce((prev, current) => {
    colsWidthOffset.push(prev)
    return prev + current.getSize()
  }, 0)
  return colsWidthOffset
}

// @ts-ignore
export const columnWidthOffsetStateInitializer: GridStateInitializer<
  Pick<DataGridProcessedProps, 'apiRef'>
> = (state, props, apiRef) => {
  const cols = apiRef?.current.tableInstance.getCenterVisibleLeafColumns()
  return {
    ...state,
    columnWidthOffset: computeWidthOffset(cols),
  }
}

export const useGridColumnWidthOffset = (
  apiRef: React.MutableRefObject<GridPrivateApiCommunity>,
  props: Pick<DataGridProcessedProps, 'columns'>
): void => {
  const setColumnWidthOffset = React.useCallback<
    GridColumnWidthOffsetApi['setColumnWidthOffset']
  >(
    (offsetArray): void => {
      apiRef.current.setState(state => {
        const currentOffset = gridColumnOffsetSelector(state)
        if (isDeepEqual(offsetArray, currentOffset)) {
          return state
        }
        return { ...state, columnWidthOffset: offsetArray }
      })
      apiRef.current.forceUpdate()
    },
    [apiRef]
  )
  const { columnSizing, columnPinning, columnOrder, columnVisibility } =
    apiRef.current.tableInstance.getState()

  React.useEffect(() => {
    const cols = apiRef.current.tableInstance.getCenterVisibleLeafColumns()
    apiRef.current.setColumnWidthOffset(computeWidthOffset(cols))
    // we want to recompute offset when columns count, column size, column order or column pinning changes
  }, [
    apiRef,
    columnSizing,
    columnPinning,
    columnOrder,
    columnVisibility,
    props.columns,
  ])
  const columnWidthOffsetApi: GridColumnWidthOffsetApi = {
    setColumnWidthOffset,
  }

  useGridApiMethod(apiRef, columnWidthOffsetApi, 'public')
}
